import * as React from 'react';
import { useState } from 'react';
import './styles.scss';

export type FeedbackRating = 'positive' | 'negative' | 'neutral';

export interface MessageFeedbackProps {
    messageId: string;
    currentRating?: FeedbackRating | null;
    onFeedback: (rating: FeedbackRating) => void;
    disabled?: boolean;
    alwaysVisible?: boolean;
    allowChange?: boolean;
}

const ThumbsUpIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </svg>
);

const ThumbsDownIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
    </svg>
);

export const MessageFeedback: React.FC<MessageFeedbackProps> = ({
    messageId,
    currentRating,
    onFeedback,
    disabled = false,
    alwaysVisible = false,
    allowChange = true,
}) => {
    const [localRating, setLocalRating] = useState<FeedbackRating | null>(currentRating || null);

    const handleFeedback = (rating: FeedbackRating) => {
        // If already rated and allowChange is false, do nothing
        if (localRating && !allowChange) {
            return;
        }

        // State transitions:
        // - positive/negative → same button → neutral (deselect/undo)
        // - positive/negative → different button → new value (change)
        // - neutral → any button → that value (re-rate)
        // Note: neutral state has no UI button, so neutral→neutral path never occurs
        if (localRating === rating && allowChange) {
            setLocalRating('neutral');
            onFeedback('neutral');
            return;
        }

        setLocalRating(rating);
        onFeedback(rating);
    };

    const isPositiveSelected = localRating === 'positive';
    const isNegativeSelected = localRating === 'negative';
    const isDisabled = disabled || (!!localRating && !allowChange);

    return (
        <div
            className={`rw-message-feedback ${alwaysVisible ? 'rw-message-feedback--always-visible' : ''}`}
            data-message-id={messageId}
        >
            <button
                type="button"
                className={`rw-feedback-btn rw-feedback-btn--positive ${
                    isPositiveSelected ? 'rw-feedback-btn--selected' : ''
                }`}
                onClick={() => handleFeedback('positive')}
                disabled={isDisabled}
                aria-label="Helpful"
                aria-pressed={isPositiveSelected}
                title="This was helpful"
            >
                <ThumbsUpIcon filled={isPositiveSelected} />
            </button>
            <button
                type="button"
                className={`rw-feedback-btn rw-feedback-btn--negative ${
                    isNegativeSelected ? 'rw-feedback-btn--selected' : ''
                }`}
                onClick={() => handleFeedback('negative')}
                disabled={isDisabled}
                aria-label="Not helpful"
                aria-pressed={isNegativeSelected}
                title="This was not helpful"
            >
                <ThumbsDownIcon filled={isNegativeSelected} />
            </button>
        </div>
    );
};

export default MessageFeedback;
